import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TrendingUp, Users, Heart, Home, Clock, UtensilsCrossed, Sprout, GraduationCap } from "lucide-react";

const KEYS = [
  { key: "lives_restored", label: "Lives restored", Icon: Sprout },
  { key: "active_mentors", label: "Active mentors", Icon: Users },
  { key: "youth_served", label: "Youth served", Icon: Heart },
  { key: "families_reunited", label: "Families reunited", Icon: Home },
  { key: "volunteer_hours", label: "Volunteer hours", Icon: Clock },
  { key: "meals_served", label: "Meals served", Icon: UtensilsCrossed },
  { key: "programs_completed", label: "Programs completed", Icon: TrendingUp },
  { key: "scholarships_awarded", label: "Scholarships awarded", Icon: GraduationCap },
];

export default function Impact() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/impact-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="impact-page">
      <section className="section">
        <div className="container-editorial">
          <div className="eyebrow">Impact dashboard</div>
          <h1 className="mt-4 font-serif text-5xl leading-[1.05] text-ink md:text-6xl">
            What restoration <em className="not-italic text-clay">actually</em> looks like.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">
            Transparency is a value, not a marketing tactic. Every quarter we publish these numbers, audited by
            our board and independent CPAs.
          </p>

          <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {KEYS.map(({ key, label, Icon }, i) => (
              <div
                key={key}
                data-testid={`impact-${key}`}
                className={`rounded-2xl border border-clay/15 bg-white p-7 ${i === 0 || i === 5 ? "lg:col-span-2 lg:bg-oat-100" : ""}`}
              >
                <Icon className="h-5 w-5 text-clay" />
                <div className="mt-5 font-serif text-5xl text-ink md:text-6xl">
                  {stats ? Number(stats[key]).toLocaleString() : "—"}
                </div>
                <div className="mt-2 text-sm font-semibold uppercase tracking-wider text-ink-muted">{label}</div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-12">
            <div className="rounded-3xl border border-clay/15 bg-white p-8 lg:col-span-8">
              <div className="eyebrow">Where the money goes</div>
              <h3 className="mt-2 font-serif text-2xl text-ink">92¢ of every dollar reaches programs.</h3>
              <div className="mt-6 space-y-4">
                {[
                  { label: "Programs & participants", pct: 92, color: "bg-clay" },
                  { label: "Administration", pct: 5, color: "bg-sage" },
                  { label: "Fundraising", pct: 3, color: "bg-clay-200" },
                ].map((b) => (
                  <div key={b.label}>
                    <div className="flex items-center justify-between text-sm text-ink">
                      <span className="font-semibold">{b.label}</span>
                      <span>{b.pct}%</span>
                    </div>
                    <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-oat-100">
                      <div className={`h-full rounded-full ${b.color}`} style={{ width: `${b.pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-sage/25 bg-sage p-8 text-oat lg:col-span-4">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-oat/80">Certified</div>
              <h3 className="mt-2 font-serif text-2xl">501(c)(3) nonprofit</h3>
              <p className="mt-3 text-sm text-oat/85">Audited annually. Our latest Form 990 is available on request.</p>
              <p className="mt-6 text-xs text-oat/70">
                Last updated: {stats?.last_updated ? new Date(stats.last_updated).toLocaleDateString() : "—"}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
